import {
	Company,
	Notification,
	User,
	currentSession,
	getNotifications,
	markNotificationAsRead,
	markAllNotificationsAsRead,
	ReportDesignCategoryEnum,
	getAllowedReportDesignCategoriesForUser,
} from "@q-food/q-food";
import { useStorage } from "@vueuse/core";
import { socketClient } from "~utils/websocket.ts";
import { utcDate } from "~utils/Date.ts";
import { error } from "@formkit/core";

export const useUserStore = defineStore("User", () => {
	// TODO: Fetch user settings async
	const activeCompany = ref<Company | null>(null);
	const activeUser = ref<(User & { allowedReportDesignCategories: string[] }) | null>(null);
	const activePermissions = ref<string[]>([]);
	const userNotifications = ref<Notification[]>([]);
	const sessionKey = useStorage<string | null>("qfood-session-token", null);
	const authenticated = computed(() => sessionKey.value != null);

	async function setToken(newSessionKey: string | null) {
		sessionKey.value = newSessionKey;

		if (newSessionKey == null) {
			activeUser.value = null;
			activeCompany.value = null;
		} else {
			await refreshUserInfo();
		}
	}

	async function refreshUserInfo() {
		const { user, company, permissions, notifications } = await currentSession().catch((response) => {
			switch (response.status) {
				case 403:
					// no need to show message
					break;
				default:
					throw Error(response);
			}
		});

		activeCompany.value = company ?? null;
		activeUser.value = user ?? null;
		activePermissions.value = permissions;
		userNotifications.value = notifications;

		if (company != null && company.settings.primaryThemeColor != null) {
			const root = document.documentElement;
			root.style.setProperty("--primary-theme-color", company.settings.primaryThemeColor);
		}

		if (activeUser.value !== null) {
			socketClient.connectHeaders = {
				"x-auth-token": localStorage.getItem("qfood-session-token")!,
			};
			socketClient.activate();

			await getAllowedReportDesignCategoriesForUser()
				.then((response) => {
					activeUser.value.allowedReportDesignCategories = response;
				})
				.catch((err) => {
					console.log(err);
					const errorMessage = err.message ?? "Er is iets misgegaan met het ophalen van de toegestane categorieën";
					error(errorMessage);
				});
		}
	}

	async function refreshNotifications() {
		const n = await getNotifications();
		userNotifications.value = n;
	}

	async function readNotification(notification: Notification) {
		const notif = userNotifications.value.find((it) => it.id == notification.id);
		notif.readAt = utcDate().format();

		await markNotificationAsRead(notification.id);
	}

	async function readAllNotifications() {
		for (const notif of userNotifications.value) {
			notif.readAt = utcDate().format();
		}

		await markAllNotificationsAsRead();
	}

	function isAllowed(privilege: string) {
		if (activeUser.value == null) {
			return false;
		}

		return activePermissions.value.includes(privilege) || activeUser.value.isAdmin;
	}

	function getPrinterId(category: ReportDesignCategoryEnum | null): string | undefined {
		const preference =
			activeUser.value?.settings.printerPreferences.find((it) => it.category == category) ??
			activeUser.value?.settings.printerPreferences.find((it) => it.category == null);
		return preference?.printerId;
	}

	return {
		activeCompany: computed(() => {
			if (!activeCompany.value) {
				throw new Error("No active company");
			}

			return activeCompany.value;
		}),
		activeOwner: computed(() => {
			const owner = activeCompany.value?.defaultOwner;

			if (owner == null) {
				throw new Error("No default owner for active company!");
			}

			return owner;
		}),
		activeUser: readonly(activeUser),
		automaticRecalculation: computed(() => {
			if (activeUser.value == null) return [];

			return Array.of(...activeUser.value.settings.automaticRecalculation.values());
		}),
		smartTabs: computed(() => {
			if (activeUser.value == null) return [];

			return Array.of(...activeUser.value.settings.smartTabs.values());
		}),
		authenticated: readonly(authenticated),
		permissions: readonly(activePermissions),
		notifications: readonly(userNotifications),
		sessionKey: readonly(sessionKey),
		isAllowed,
		setToken,
		readNotification,
		readAllNotifications,
		refreshNotifications,
		refreshUserInfo,
		getPrinterId,
	};
});
